import * as i0 from '@angular/core';
import { Component, ViewEncapsulation, ChangeDetectionStrategy, InjectionToken, Injectable, Inject, NgModule, Optional, SkipSelf } from '@angular/core';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i1$1 from '@angular/common/http';
import { HttpClientModule, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import * as i1 from 'ngx-toastr';
import { Toast, ToastrModule } from 'ngx-toastr';
import { trigger, state, style, transition, animate, keyframes } from '@angular/animations';
import { ColumnMode } from '@swimlane/ngx-datatable';
import { Subject, BehaviorSubject, takeUntil, debounceTime, distinctUntilChanged, switchMap, tap, combineLatest, filter, firstValueFrom, Observable } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { __awaiter } from 'tslib';
import { saveAs } from 'file-saver';
import { format, subHours, isFuture } from 'date-fns';
import * as i1$2 from '@angular/router';
import * as i2$1 from '@auth0/angular-jwt';

class ToastrComponent extends Toast {
    // constructor is only necessary when not using AoT
    constructor(toastrService, toastPackage) {
        super(toastrService, toastPackage);
        this.toastrService = toastrService;
        this.toastPackage = toastPackage;
    }
}
ToastrComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ToastrComponent, deps: [{ token: i1.ToastrService }, { token: i1.ToastPackage }], target: i0.ɵɵFactoryTarget.Component });
ToastrComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: ToastrComponent, selector: "hexa-toastr", usesInheritance: true, ngImport: i0, template: "<div class=\"d-flex align-items-center\">\n  <div class=\"toast-icon toast-icon-success flex-shrink-0 mr-3\">\n    <svg class=\"fill-success-600\" height=\"32\" viewBox=\"0 0 24 24\" width=\"32\">\n      <path d=\"M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z\" stroke=\"none\"/>\n    </svg>\n  </div>\n  <div class=\"toast-icon toast-icon-error flex-shrink-0 mr-3\">\n    <svg class=\"fill-danger-600\" height=\"32\" viewBox=\"0 0 24 24\" width=\"32\">\n      <path\n        d=\"M13,13H11V7H13M13,17H11V15H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z\"\n        stroke=\"none\"/>\n    </svg>\n  </div>\n  <div class=\"flex-grow-1\">\n    <h6 *ngIf=\"title\" class=\"mb-2 font-weight-medium\">{{ title }}</h6>\n    <div *ngIf=\"message && options.enableHtml\" [class]=\"options.messageClass\" [innerHTML]=\"message\"></div>\n    <div *ngIf=\"message && !options.enableHtml\" [class]=\"options.messageClass\">{{ message }}</div>\n  </div>\n</div>\n", styles: [""], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], animations: [
        trigger("flyInOut", [
            state("inactive", style({
                opacity: 0,
            })),
            transition("inactive => active", animate("400ms 800ms ease-out", keyframes([
                style({
                    transform: "translate3d(100%, 0, 0) skewX(-30deg)",
                    opacity: 0,
                }),
                style({
                    transform: "skewX(20deg)",
                    opacity: 1,
                }),
                style({
                    transform: "skewX(-5deg)",
                    opacity: 1,
                }),
                style({
                    transform: "none",
                    opacity: 1,
                }),
            ]))),
            transition("active => removed", animate("400ms ease-out", keyframes([
                style({
                    opacity: 1,
                }),
                style({
                    transform: "translate3d(100%, 0, 0)",
                    opacity: 0,
                }),
            ]))),
        ]),
    ], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ToastrComponent, decorators: [{
            type: Component,
            args: [{ selector: "hexa-toastr", encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, animations: [
                        trigger("flyInOut", [
                            state("inactive", style({
                                opacity: 0,
                            })),
                            transition("inactive => active", animate("400ms 800ms ease-out", keyframes([
                                style({
                                    transform: "translate3d(100%, 0, 0) skewX(-30deg)",
                                    opacity: 0,
                                }),
                                style({
                                    transform: "skewX(20deg)",
                                    opacity: 1,
                                }),
                                style({
                                    transform: "skewX(-5deg)",
                                    opacity: 1,
                                }),
                                style({
                                    transform: "none",
                                    opacity: 1,
                                }),
                            ]))),
                            transition("active => removed", animate("400ms ease-out", keyframes([
                                style({
                                    opacity: 1,
                                }),
                                style({
                                    transform: "translate3d(100%, 0, 0)",
                                    opacity: 0,
                                }),
                            ]))),
                        ]),
                    ], preserveWhitespaces: false, template: "<div class=\"d-flex align-items-center\">\n  <div class=\"toast-icon toast-icon-success flex-shrink-0 mr-3\">\n    <svg class=\"fill-success-600\" height=\"32\" viewBox=\"0 0 24 24\" width=\"32\">\n      <path d=\"M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z\" stroke=\"none\"/>\n    </svg>\n  </div>\n  <div class=\"toast-icon toast-icon-error flex-shrink-0 mr-3\">\n    <svg class=\"fill-danger-600\" height=\"32\" viewBox=\"0 0 24 24\" width=\"32\">\n      <path\n        d=\"M13,13H11V7H13M13,17H11V15H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z\"\n        stroke=\"none\"/>\n    </svg>\n  </div>\n  <div class=\"flex-grow-1\">\n    <h6 *ngIf=\"title\" class=\"mb-2 font-weight-medium\">{{ title }}</h6>\n    <div *ngIf=\"message && options.enableHtml\" [class]=\"options.messageClass\" [innerHTML]=\"message\"></div>\n    <div *ngIf=\"message && !options.enableHtml\" [class]=\"options.messageClass\">{{ message }}</div>\n  </div>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: i1.ToastrService }, { type: i1.ToastPackage }]; } });

const LIST_CONFIG_TOKEN = new InjectionToken('LIST_CONFIG_TOKEN');
const API_SERVICE_CONFIG_TOKEN = new InjectionToken('API_SERVICE_CONFIG_TOKEN');
const BASE_API_URL_TOKEN = new InjectionToken('BASE_API_URL');
const STRIPE_KEY_TOKEN = new InjectionToken('STRIPE_KEY');
const PRODUCTION_TOKEN = new InjectionToken('PRODUCTION');
const APP_NAME = new InjectionToken('APP');

class HexaStorageService {
    constructor() {
        this.onSubject = new Subject();
        this.changes$ = this.onSubject.asObservable();
        this.sessionBased = false; // Turned off by default
    }
    getStorage() {
        return this.sessionBased ? sessionStorage : localStorage;
    }
    save(key, value) {
        try {
            const storage = this.getStorage();
            if (key === 'auth_token') {
                storage.setItem(key, value);
            }
            else {
                const serializedValue = btoa(JSON.stringify(value));
                storage.setItem(key, serializedValue);
                this.onSubject.next({ key, value });
            }
        }
        catch (e) {
            console.error('Saving key error', key, `Error saving to storage: ${e}`);
        }
    }
    get(key) {
        try {
            const storage = this.getStorage();
            if (key === 'auth_token')
                return storage.getItem(key);
            const serializedValue = storage.getItem(key);
            if (serializedValue === null)
                return null;
            return JSON.parse(atob(serializedValue));
        }
        catch (e) {
            console.error('Get key error', key, `Error getting data from storage: ${e}`);
            return null;
        }
    }
    remove(key) {
        this.getStorage().removeItem(key);
        this.onSubject.next({ key, value: null });
    }
    load(storage) {
        const targetStorage = this.getStorage();
        if (!storage)
            return;
        for (const item of storage) {
            targetStorage.setItem(item.key, btoa(JSON.stringify(item.value)));
        }
    }
    toJson() {
        const storageItems = [];
        const storage = this.getStorage();
        for (let i = 0; i < storage.length; i++) {
            const key = storage.key(i);
            if (!key || key === 'auth_token')
                continue;
            try {
                const encodedValue = storage.getItem(key);
                const decodedValue = encodedValue ? JSON.parse(atob(encodedValue)) : null;
                storageItems.push({ key, value: decodedValue });
            }
            catch (e) {
                console.error('Error parsing storage item:', key, e);
            }
        }
        return storageItems;
    }
    clear() {
        this.getStorage().clear();
        this.onSubject.next({ key: 'all', value: null });
    }
    ngOnDestroy() {
        this.stop();
    }
    stop() {
        this.onSubject.complete();
    }
}
HexaStorageService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HexaStorageService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
HexaStorageService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HexaStorageService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HexaStorageService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return []; } });

class HexaColumnService {
    constructor(storageService) {
        this.storageService = storageService;
    }
    // Initialize columns based on original and personalization settings
    initializeColumns(originalColumns, personalizedColumns) {
        if (personalizedColumns.length === 0) {
            return originalColumns.map((column, index) => {
                const data = Object.assign(Object.assign({}, column), { name: column.name, hide: false, required: column.required, position: index });
                if (column.width)
                    data['width'] = column.width;
                return data;
            });
        }
        else {
            return originalColumns.map((column, index) => {
                const personalizedColumn = personalizedColumns.find(item => item.name === column.name);
                if (personalizedColumn) {
                    const data = Object.assign(Object.assign({}, column), { position: (personalizedColumn === null || personalizedColumn === void 0 ? void 0 : personalizedColumn.position) ? personalizedColumn.position : index, hide: column.required ? false : personalizedColumn.hide });
                    if (personalizedColumn.width)
                        data['width'] = personalizedColumn.width;
                    return data;
                }
                return column;
            }).filter(column => !column.hide).sort((a, b) => a.position - b.position);
        }
    }
    // Resize a column
    resizeColumn(personalized, columnName, newValue) {
        const column = personalized.find(p => p.name === columnName);
        if (column) {
            column.width = newValue;
        }
    }
    // Reorder columns
    reorderColumns(personalized, originalPosition, newPosition) {
        const temp = personalized[originalPosition];
        personalized[originalPosition] = personalized[newPosition];
        personalized[newPosition] = temp;
    }
    // Toggle column visibility
    visibilityChange(columns, col) {
        const column = columns.find(c => c.name === col.name);
        if (column) {
            column.hide = !column.hide;
        }
    }
    // Reset columns to their default state
    resetColumns(originalColumns) {
        return originalColumns.map((column, index) => ({
            name: column.name,
            hide: false,
            required: column.required,
            width: column.width,
            position: index,
        }));
    }
}
HexaColumnService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HexaColumnService, deps: [{ token: HexaStorageService }], target: i0.ɵɵFactoryTarget.Injectable });
HexaColumnService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HexaColumnService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HexaColumnService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: HexaStorageService }]; } });

class HexaBaseComponent {
    constructor() {
        this.destroy$ = new Subject();
    }
    ngOnInit() {
    }
    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.complete();
    }
}
HexaBaseComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HexaBaseComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
HexaBaseComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: HexaBaseComponent, selector: "ng-component", ngImport: i0, template: "hexa-base", isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HexaBaseComponent, decorators: [{
            type: Component,
            args: [{
                    template: "hexa-base",
                }]
        }], ctorParameters: function () { return []; } });

class HexaDetailComponent extends HexaBaseComponent {
    constructor(injector) {
        super();
        this.pageChange$ = new BehaviorSubject(false);
        this.sortChange$ = new BehaviorSubject(false);
        this.limit = 20;
        this.offset = 0;
        this.oDataQuery$ = new BehaviorSubject({
            $top: this.limit,
            $skip: 0,
            $orderby: '',
            $inlinecount: 'allpages',
        });
        this.orderBy = 'updatedAt desc';
        this.oDataQuery$.next(Object.assign(Object.assign({}, this.oDataQuery$.getValue()), { $orderby: this.orderBy }));
    }
    ngOnChanges(changes) {
        if ('selectedOrganization$' in changes && !changes['selectedOrganization$'].isFirstChange()) {
            this.commonCombineLatest();
        }
    }
    handleResponse(body) {
        setTimeout(() => {
            window.dispatchEvent(new Event("resize"));
        }, 10);
    }
    commonPipe(responseHandler) {
        return (source) => source.pipe(takeUntil(this.destroy$), debounceTime(100), distinctUntilChanged(), switchMap(responseHandler), tap(this.handleResponse.bind(this)));
    }
    preparePagingParams(formFilterName, filterQueries) {
        this.pagingParams = this.oDataQuery$.getValue();
        this.pagingParams.$skip = this.limit * this.offset;
        if (filterQueries && filterQueries.length > 0) {
            this.pagingParams.$filter = filterQueries.join(" and ");
        }
        else {
            delete this.pagingParams.$filter;
        }
    }
    onPage(event) {
        const oDataQuery = this.oDataQuery$.getValue();
        this.pageChange$.next(true);
        this.offset = event.offset;
        this.oDataQuery$.next(Object.assign(Object.assign({}, oDataQuery), { $skip: this.limit * event.offset }));
    }
    onSort(event) {
        const oDataQuery = this.oDataQuery$.getValue();
        this.sortChange$.next(true);
        this.offset = 0;
        if (event.sorts && event.sorts.length > 0) {
            this.oDataQuery$.next(Object.assign(Object.assign({}, oDataQuery), { $orderby: `${event.sorts[0].prop} ${event.sorts[0].dir}` }));
        }
    }
    //move to project list
    commonCombineLatest() {
        return combineLatest([
            this.selectedOrganization$.pipe(tap(() => (this.offset = 0)), filter(organization => organization && (organization.id || organization.organizationId)) // Only proceed if organization and organization.id are not null
            ),
            this.formFilter.valueChanges.pipe(debounceTime(600), startWith(this.formFilter.value), tap(() => (this.offset = 0))),
            this.pageChange$.pipe(startWith(true), debounceTime(200)),
            this.sortChange$.pipe(startWith(true), debounceTime(200)),
        ]);
    }
    commonCombineNoOrgLatest() {
        return combineLatest([
            this.formFilter.valueChanges.pipe(startWith(this.formFilter.value), tap(() => (this.offset = 0)), debounceTime(600)),
            this.pageChange$.pipe(startWith(true), debounceTime(200)),
            this.sortChange$.pipe(startWith(true), debounceTime(200)),
        ]);
    }
}
HexaDetailComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HexaDetailComponent, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.Component });
HexaDetailComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: HexaDetailComponent, selector: "hexa-core-detail", usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<p>detail works!</p>\n", styles: [""] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HexaDetailComponent, decorators: [{
            type: Component,
            args: [{ selector: 'hexa-core-detail', template: "<p>detail works!</p>\n" }]
        }], ctorParameters: function () { return [{ type: i0.Injector }]; } });

class HexaListComponent extends HexaDetailComponent {
    constructor(injector, config, storageService = injector.get(HexaStorageService), columnService = injector.get(HexaColumnService)) {
        super(injector);
        this.storageService = storageService;
        this.columnService = columnService;
        this.original = [];
        this.columns = [];
        this.personalized = [];
        this.ColumnMode = ColumnMode;
        this.datagrid = `DataGrid:${config.dataGrid}`;
        super.orderBy = config.orderBy || super.orderBy;
    }
    loadFilterForm(key) {
        const formValue = this.clean(this.storageService.get(key));
        if (formValue) {
            //formValue
            // @ts-ignore
            this.formFilter.patchValue(Object.assign({}, formValue));
        }
    }
    saveFilterForm(key) {
        this.storageService.save(key, this.formFilter.value);
    }
    loadPersonalization() {
        this.personalized = this.storageService.get(this.datagrid) || [];
        if (this.personalized.length === 0) {
            this.personalized = this.columnService.initializeColumns(this.columns, this.personalized);
            this.savePersonalized();
        }
        this.applyPersonalizationToColumns();
    }
    resize(event) {
        if (!event.column || !event.newValue || !this.personalized)
            return;
        this.columnService.resizeColumn(this.personalized, event.column.name, event.newValue);
        this.savePersonalized();
    }
    reorder(event) {
        if (!event.column || event.newValue === undefined || event.prevValue === undefined)
            return;
        this.columnService.reorderColumns(this.personalized, event.prevValue, event.newValue);
        this.applyPersonalizationToColumns();
        this.savePersonalized();
    }
    resetColumns() {
        if (!this.columns || !this.original)
            return;
        this.personalized = this.columnService.resetColumns(this.original);
        this.columns = this.original;
        this.savePersonalized();
    }
    visibilityChange(col) {
        const isChecked = this.isChecked(col);
        col.hide = isChecked;
        this.columns.map((x, index) => {
            const selectedColumn = this.personalized.filter(p => p.name === x.name);
            if (selectedColumn.length) {
                x['position'] = selectedColumn[0].position;
            }
            else {
                x['position'] = index;
            }
        });
        if (isChecked) {
            this.columns = this.columns.filter(c => {
                return c.name !== col.name;
            });
        }
        else {
            const element = this.original.filter(x => x.name === col.name);
            if (element.length)
                col = Object.assign(Object.assign({}, col), element[0]);
            const columns = [...this.columns, col];
            this.columns = columns.sort((a, b) => a.position - b.position);
        }
        this.savePersonalized();
    }
    deepClone(obj) {
        if (obj)
            return obj;
        return JSON.parse(JSON.stringify(obj));
    }
    refresh() {
        this.formFilter.setValue(this.formFilter.value, { emitEvent: true });
    }
    formatPersonalized(columns) {
        return columns.filter((c) => c.name).map((column) => {
            return {
                name: column.name,
                width: column.width,
                position: column.position,
                hide: column.hide,
            };
        });
    }
    preparePagingParams(formFilterName, filterQueries) {
        if (formFilterName) {
            this.storageService.save(formFilterName, this.formFilter.value);
        }
        super.preparePagingParams(formFilterName, filterQueries);
    }
    clean(json) {
        if (Array.isArray(json)) {
            return json.map(item => this.clean(item)).filter(item => item !== null && item !== undefined);
        }
        else if (json !== null && typeof json === 'object') {
            Object.keys(json).forEach(key => {
                const value = json[key];
                if (value === null || value === undefined || (typeof value === 'object' && Object.keys(value).length === 0 && !Array.isArray(value))) {
                    delete json[key];
                }
                else {
                    json[key] = this.clean(value);
                }
            });
            return (Object.keys(json).length === 0 && json.constructor === Object) ? null : json;
        }
        return json;
    }
    isChecked(col) {
        return (this.columns.find(c => {
            return c.name === col.name;
        }) !== undefined);
    }
    applyPersonalizationToColumns() {
        this.original = this.deepClone(this.columns);
        this.columns = this.columnService.initializeColumns(this.columns, this.personalized);
    }
    savePersonalized() {
        this.storageService.save(this.datagrid, this.formatPersonalized(this.personalized));
    }
}
HexaListComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HexaListComponent, deps: [{ token: i0.Injector }, { token: LIST_CONFIG_TOKEN }, { token: HexaStorageService }, { token: HexaColumnService }], target: i0.ɵɵFactoryTarget.Component });
HexaListComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: HexaListComponent, selector: "hexa-core-list", usesInheritance: true, ngImport: i0, template: "", styles: [""] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HexaListComponent, decorators: [{
            type: Component,
            args: [{ selector: 'hexa-core-list', template: "" }]
        }], ctorParameters: function () {
        return [{ type: i0.Injector }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [LIST_CONFIG_TOKEN]
                    }] }, { type: HexaStorageService }, { type: HexaColumnService }];
    } });

function throwIfAlreadyLoaded(parentModule, moduleName) {
    if (parentModule) {
        throw new Error(`${moduleName} has already been loaded. Import ${moduleName} in the AppModule only.`);
    }
}
class HexaCoreModule {
    constructor(parentModule) {
        throwIfAlreadyLoaded(parentModule, "HexaCoreModule");
    }
    static forRoot(config) {
        return {
            ngModule: HexaCoreModule,
            providers: [
                {
                    provide: API_SERVICE_CONFIG_TOKEN,
                    useValue: config,
                },
            ],
        };
    }
}
HexaCoreModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HexaCoreModule, deps: [{ token: HexaCoreModule, optional: true, skipSelf: true }], target: i0.ɵɵFactoryTarget.NgModule });
HexaCoreModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: HexaCoreModule, declarations: [ToastrComponent,
        HexaListComponent,
        HexaDetailComponent], imports: [CommonModule,
        HttpClientModule,
        BrowserAnimationsModule, i1.ToastrModule], exports: [ToastrComponent,
        HexaListComponent,
        HexaDetailComponent] });
HexaCoreModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HexaCoreModule, imports: [CommonModule,
        HttpClientModule,
        BrowserAnimationsModule,
        ToastrModule.forRoot({
            toastComponent: ToastrComponent,
            enableHtml: true,
            timeOut: 10000,
        })] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HexaCoreModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        HttpClientModule,
                        BrowserAnimationsModule,
                        ToastrModule.forRoot({
                            toastComponent: ToastrComponent,
                            enableHtml: true,
                            timeOut: 10000,
                        }),
                    ],
                    declarations: [
                        ToastrComponent,
                        HexaListComponent,
                        HexaDetailComponent,
                    ],
                    exports: [
                        ToastrComponent,
                        HexaListComponent,
                        HexaDetailComponent,
                    ],
                    providers: []
                }]
        }], ctorParameters: function () {
        return [{ type: HexaCoreModule, decorators: [{
                        type: Optional
                    }, {
                        type: SkipSelf
                    }] }];
    } });

class HexaApiService {
    constructor(config, http) {
        this.http = http;
        this.isDownloadingSubject = new BehaviorSubject(null);
        this.isDownloading$ = this.isDownloadingSubject.asObservable();
        this.isUploadingSubject = new BehaviorSubject(null);
        this.isUploading$ = this.isUploadingSubject.asObservable();
        this.isLoadingSubject = new BehaviorSubject(false);
        this.isLoading$ = this.isLoadingSubject.asObservable();
        this.production = false;
        this.stripeKey = "";
        this.headers = new HttpHeaders();
        this.baseApiUrl = "";
        this.appName = "";
        if (config) {
            this.baseApiUrl = config.baseApiUrl;
            this.production = config.production;
            this.stripeKey = config.stripeKey;
            this.appName = config.app;
        }
    }
    // CRUD methods
    get(path, params, locator = true) {
        return __awaiter(this, void 0, void 0, function* () {
            this.isLoadingSubject.next(locator);
            return firstValueFrom(this.http.get(`${this.baseApiUrl}/${path}`, {
                headers: this.headers,
                observe: "response",
                params: params,
            })).catch((error) => {
                throw error;
            }).finally(() => {
                this.isLoadingSubject.next(null);
            });
        });
    }
    post(path, payload, locator = true) {
        return __awaiter(this, void 0, void 0, function* () {
            this.isLoadingSubject.next(locator);
            return firstValueFrom(this.http.post(`${this.baseApiUrl}/${path}`, payload, {
                headers: this.headers,
                observe: "response",
            })).catch((error) => {
                throw error;
            }).finally(() => {
                this.isLoadingSubject.next(null);
            });
        });
    }
    put(path, payload, locator = true) {
        return __awaiter(this, void 0, void 0, function* () {
            this.isLoadingSubject.next(locator);
            return firstValueFrom(this.http.put(`${this.baseApiUrl}/${path}`, payload, {
                headers: this.headers,
                observe: "response",
            })).catch((error) => {
                throw error;
            }).finally(() => {
                this.isLoadingSubject.next(null);
            });
        });
    }
    delete(path, locator = true) {
        return __awaiter(this, void 0, void 0, function* () {
            this.isLoadingSubject.next(locator);
            return firstValueFrom(this.http.delete(`${this.baseApiUrl}/${path}`, {
                headers: this.headers,
                observe: "response",
            })).catch((error) => {
                throw error;
            }).finally(() => {
                this.isLoadingSubject.next(null);
            });
        });
    }
    media(path, params, locator) {
        return __awaiter(this, void 0, void 0, function* () {
            this.isDownloadingSubject.next(locator);
            const response = yield firstValueFrom(this.http.get(`${this.baseApiUrl}/${path}`, {
                headers: this.headers,
                responseType: "blob",
                observe: "response",
                params: params,
            })).catch((error) => {
                throw error;
            }).finally(() => {
                this.isDownloadingSubject.next(null);
            });
            return this.parseMediaResponse(response);
        });
    }
    openBlob(path, params, locator) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.media(path, params, locator);
            const fileURL = URL.createObjectURL(response.blob);
            window.open(fileURL);
        });
    }
    downloadBlob(path, params, filename, locator) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.media(path, params, locator);
            saveAs(response.blob, filename || response.name);
            return response;
        });
    }
    getExternal(url, params, locator = true) {
        return __awaiter(this, void 0, void 0, function* () {
            this.isLoadingSubject.next(locator);
            return firstValueFrom(this.http.get(`${url}`, {
                headers: this.headers,
                observe: "response",
                params: params,
            })).catch((error) => {
                throw error;
            }).finally(() => {
                this.isLoadingSubject.next(null);
            });
        });
    }
    upload(path, media, locator) {
        return __awaiter(this, void 0, void 0, function* () {
            const payload = new FormData();
            if (Array.isArray(media)) {
                // Handle the case where media is an array of objects
                media.forEach((item) => {
                    if (item.file) {
                        payload.append('file', item.file, item.file.name); // Use 'file' for all files
                    }
                    if (item.mimeType) {
                        payload.append('mimeType', item.mimeType); // Only if needed
                    }
                    if (item.type) {
                        payload.append('type', item.type);
                    }
                });
            }
            else {
                // Handle the case where media is a single object
                if (media.file) {
                    payload.append('file', media.file, media.file.name); // Use 'file' for the single file
                }
                if (media.mimeType) {
                    payload.append('mimeType', media.mimeType); // Only if needed
                }
                if (media.type) {
                    payload.append('type', media.type);
                }
            }
            this.isUploadingSubject.next(locator);
            return firstValueFrom(this.http.post(`${this.baseApiUrl}/${path}`, payload, {
                headers: this.headers,
                observe: 'response',
            })).catch((error) => {
                throw error;
            }).finally(() => {
                this.isUploadingSubject.next(false);
            });
        });
    }
    parseMediaResponse(response) {
        var _a, _b, _c;
        const contentType = response.headers.get("Content-Type");
        const contentDisposition = response.headers.get("Content-Disposition");
        const ext = ((_a = contentType === null || contentType === void 0 ? void 0 : contentType.split(";")[1]) === null || _a === void 0 ? void 0 : _a.split("=")[1]) || "";
        const fileName = ((_c = (_b = contentDisposition === null || contentDisposition === void 0 ? void 0 : contentDisposition.split(";")[1]) === null || _b === void 0 ? void 0 : _b.split("=")[1]) === null || _c === void 0 ? void 0 : _c.replace(/"/g, "")) || "";
        return { ext, name: fileName, blob: response.body };
    }
}
HexaApiService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HexaApiService, deps: [{ token: API_SERVICE_CONFIG_TOKEN }, { token: i1$1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
HexaApiService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HexaApiService, providedIn: "root" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HexaApiService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: "root",
                }]
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [API_SERVICE_CONFIG_TOKEN]
                    }] }, { type: i1$1.HttpClient }];
    } });

class HexaTitleService {
    constructor() {
        this._title = new BehaviorSubject('Welcome!');
        this.title$ = this._title.asObservable();
        this._previousUrl = new BehaviorSubject(null);
        this.previousUrl$ = this._previousUrl.asObservable();
    }
    changeTitle(title) {
        this._title.next(title);
    }
    setPreviousUrl(url) {
        this._previousUrl.next(url);
    }
}
HexaTitleService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HexaTitleService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
HexaTitleService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HexaTitleService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HexaTitleService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return []; } });

class HexaAuthGuardService {
    constructor(router, jwtHelperService, storageService) {
        this.router = router;
        this.jwtHelperService = jwtHelperService;
        this.storageService = storageService;
    }
    canActivate(next, state) {
        const token = this.storageService.get('auth_token');
        const expirationDate = token ? this.jwtHelperService.decodeToken(token).expiration : format(subHours(new Date(), 2), 'X');
        if (isFuture(expirationDate)) {
            return true;
        }
        return this.router.parseUrl('/auth');
    }
    canActivateChild(next, state) {
        return this.canActivate(next, state);
    }
}
HexaAuthGuardService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HexaAuthGuardService, deps: [{ token: i1$2.Router }, { token: i2$1.JwtHelperService }, { token: HexaStorageService }], target: i0.ɵɵFactoryTarget.Injectable });
HexaAuthGuardService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HexaAuthGuardService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HexaAuthGuardService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1$2.Router }, { type: i2$1.JwtHelperService }, { type: HexaStorageService }]; } });

class ApiServiceConfig {
    constructor(baseApiUrl, stripeKey, production, app = 'admin') {
        this.baseApiUrl = baseApiUrl;
        this.stripeKey = stripeKey;
        this.production = production;
        this.app = app;
    }
}
ApiServiceConfig.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ApiServiceConfig, deps: [{ token: BASE_API_URL_TOKEN }, { token: STRIPE_KEY_TOKEN }, { token: PRODUCTION_TOKEN }, { token: APP_NAME, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
ApiServiceConfig.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ApiServiceConfig, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ApiServiceConfig, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [BASE_API_URL_TOKEN]
                    }] }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [STRIPE_KEY_TOKEN]
                    }] }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [PRODUCTION_TOKEN]
                    }] }, { type: undefined, decorators: [{
                        type: Optional
                    }, {
                        type: Inject,
                        args: [APP_NAME]
                    }] }];
    } });

function HexaWithLoadingAndErrorHandler(Base = class {
}) {
    return class extends Base {
        constructor(...args) {
            super(...args);
        }
        get isLoading$() {
            // @ts-ignore
            if (this['apiService']) {
                // @ts-ignore
                return this['apiService'].isLoading$;
            }
            console.error('ApiService is not defined in this class or isLoading$ is not defined in ApiService.');
            return new BehaviorSubject(false); // Fallback behavior
        }
        get isLoadingSubject() {
            // @ts-ignore
            if (this['apiService']) {
                // @ts-ignore
                return this['apiService'].isLoadingSubject;
            }
            console.error('ApiService is not defined in this class or isLoadingSubject is not defined in ApiService.');
            // @ts-ignore
            return new BehaviorSubject(null);
        }
        get stripeKey() {
            // @ts-ignore
            return this['apiService'].stripeKey;
        }
        get appName() {
            // @ts-ignore
            return this['apiService'].appName;
        }
        get isDownloading$() {
            // @ts-ignore
            if (this['apiService']) {
                // @ts-ignore
                return this['apiService'].isDownloading$;
            }
            console.error('ApiService is not defined in this class or isDownloading$ is not defined in ApiService.');
            // @ts-ignore
            return new BehaviorSubject(null); // Fallback behavior
        }
        get isDownloadingSubject() {
            // @ts-ignore
            if (this['apiService']) {
                // @ts-ignore
                return this['apiService'].isDownloadingSubject;
            }
            console.error('ApiService is not defined in this class or isDownloadingSubject is not defined in ApiService.');
            // @ts-ignore
            return new BehaviorSubject(null);
        }
        get isUploading$() {
            // @ts-ignore
            if (this['apiService']) {
                // @ts-ignore
                return this['apiService'].isUploading$;
            }
            console.error('ApiService is not defined in this class or isUploading$ is not defined in ApiService.');
            // @ts-ignore
            return new BehaviorSubject(null); // Fallback behavior
        }
        get(path, params, locator) {
            return __awaiter(this, void 0, void 0, function* () {
                // @ts-ignore
                return this['apiService'].get(path, params, locator).catch((error) => __awaiter(this, void 0, void 0, function* () {
                    throw yield this.handleAndDisplayError(error);
                }));
            });
        }
        ;
        post(path, payload, locator) {
            return __awaiter(this, void 0, void 0, function* () {
                // @ts-ignore
                return this['apiService'].post(path, payload, locator).catch((error) => __awaiter(this, void 0, void 0, function* () {
                    throw yield this.handleAndDisplayError(error);
                }));
            });
        }
        ;
        put(path, payload, locator) {
            return __awaiter(this, void 0, void 0, function* () {
                // @ts-ignore
                return this['apiService'].put(path, payload, locator).catch((error) => __awaiter(this, void 0, void 0, function* () {
                    throw yield this.handleAndDisplayError(error);
                }));
            });
        }
        ;
        delete(path, locator) {
            return __awaiter(this, void 0, void 0, function* () {
                // @ts-ignore
                return this['apiService'].delete(path, locator).catch((error) => __awaiter(this, void 0, void 0, function* () {
                    throw yield this.handleAndDisplayError(error);
                }));
            });
        }
        ;
        media(path, params, locator) {
            return __awaiter(this, void 0, void 0, function* () {
                // @ts-ignore
                return this['apiService'].media(path, params).catch((error) => __awaiter(this, void 0, void 0, function* () {
                    throw yield this.handleAndDisplayError(error);
                }));
            });
        }
        ;
        openBlob(path, params, locator) {
            return __awaiter(this, void 0, void 0, function* () {
                // @ts-ignore
                return this['apiService'].openBlob(path, params, locator).catch((error) => __awaiter(this, void 0, void 0, function* () {
                    throw yield this.handleAndDisplayError(error);
                }));
            });
        }
        ;
        downloadBlob(path, params, filename, locator) {
            return __awaiter(this, void 0, void 0, function* () {
                // @ts-ignore
                return this['apiService'].downloadBlob(path, params, filename, locator).catch((error) => __awaiter(this, void 0, void 0, function* () {
                    throw yield this.handleAndDisplayError(error);
                }));
            });
        }
        ;
        getExternal(url, params, locator) {
            return __awaiter(this, void 0, void 0, function* () {
                // @ts-ignore
                return this['apiService'].getExternal(url, params, locator).catch((error) => __awaiter(this, void 0, void 0, function* () {
                    throw yield this.handleAndDisplayError(error);
                }));
            });
        }
        ;
        upload(path, media, locator) {
            return __awaiter(this, void 0, void 0, function* () {
                // @ts-ignore
                return this['apiService'].upload(path, media, locator).catch((error) => __awaiter(this, void 0, void 0, function* () {
                    throw yield this.handleAndDisplayError(error);
                }));
            });
        }
        ;
        handleAndDisplayError(error) {
            return __awaiter(this, void 0, void 0, function* () {
                if (!error) {
                    return { message: 'General missing error.', name: 'General Error' };
                }
                if (error.error instanceof Blob) {
                    try {
                        const text = yield error.error.text();
                        const err = JSON.parse(text);
                        return { message: err.message, name: err.name };
                    }
                    catch (parseError) {
                        throw parseError;
                    }
                }
                if (error instanceof HttpErrorResponse) {
                    return { message: error.error.message, name: error.error.name };
                }
                if (error instanceof TypeError) {
                    return { message: 'Invalid Object Type', name: 'Type Error' };
                }
                if (error === null || error === void 0 ? void 0 : error.name) {
                    return { message: error.error, name: error.name };
                }
                return { message: error, name: 'General Error' };
            });
        }
    };
}

function HexaWithPermissions(Base) {
    return class extends Base {
        constructor(...args) {
            super(...args);
            this.hasPermission$ = new Observable();
            this.checkPermission = (permission) => {
                // @ts-ignore
                if (this['permissionService']) { // Use square bracket notation to access the ApiService instance
                    // @ts-ignore
                    this.hasPermission$ = this['permissionService'].permissions$.pipe(switchMap(() => 
                    // @ts-ignore
                    this['permissionService'].hasPermission(permission)));
                }
                else {
                    console.error('NgxPermissionsService is not defined in this class.');
                }
            };
        }
    };
}

/**
 * Generated bundle index. Do not edit.
 */

export { API_SERVICE_CONFIG_TOKEN, APP_NAME, ApiServiceConfig, BASE_API_URL_TOKEN, HexaApiService, HexaAuthGuardService, HexaBaseComponent, HexaColumnService, HexaCoreModule, HexaDetailComponent, HexaListComponent, HexaStorageService, HexaTitleService, HexaWithLoadingAndErrorHandler, HexaWithPermissions, LIST_CONFIG_TOKEN, PRODUCTION_TOKEN, STRIPE_KEY_TOKEN, ToastrComponent, throwIfAlreadyLoaded };
